import { observer } from 'mobx-react';
import React, { useMemo, useState } from 'react';
import { useWrapRequest } from 'use-wrap-request';
import { Select } from '@components/select';
import { TextField } from '@components/textfield';
import { Alert, AlertDescription } from '@components/ui/alert';
import { Button } from '@components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@components/ui/dialog';
import {
    ImageType,
    MoneyValueDto,
    ProductCreateSupportRequestDto,
    ProductState
} from '@api/WalletClient';
import { Api } from '../../core/api';
import { injectTSDI } from '../../core/tsdi';
import {
    isValidNumericValue,
    parseInputToNumber,
    valueToString
} from '../../core/utils/number-input-utils';
import { WalletImageUpload } from '../components/wallet-image-upload';

interface CreateProductModalProps {
    open: boolean;
    vendorId: number;

    onClose(): void;

    onCreated(): void;
}

const defaultMoney = (currency: string, value = 0): MoneyValueDto => ({
    currencyCode: currency,
    value
});

export const CreateProductModal = observer(
    ({ open, vendorId, onClose, onCreated }: CreateProductModalProps) => {
        const { walletClient } = injectTSDI(Api);

        const [formData, setFormData] =
            useState<ProductCreateSupportRequestDto>({
                image: { objectKey: '', bucketName: '' },
                symbol: '',
                externalId: '',
                price: defaultMoney('STR'),
                priceStock: undefined,
                variantName: '',
                value: undefined,
                internalState: ProductState.DISABLED
            });

        const [faceValue, setFaceValue] = useState<MoneyValueDto | undefined>(
            undefined
        );
        const [error, setError] = useState<string | null>(null);

        const createProduct = useWrapRequest(
            async (data: ProductCreateSupportRequestDto) => {
                const result =
                    await walletClient.supportApi.vendorSupportControllerCreateProduct(
                        vendorId,
                        data
                    );
                return result.data;
            },
            {
                defaultData: null
            }
        );

        const isValid = useMemo(() => {
            return (
                !!formData.image.objectKey &&
                !!formData.price.currencyCode &&
                isValidNumericValue(formData.price.value, 0, false)
            );
        }, [formData.image.objectKey, formData.price]);

        const handlePriceValueChange = (raw: string) => {
            const numericValue = parseInputToNumber(raw, true);
            setFormData((prev) => ({
                ...prev,
                price: {
                    ...prev.price,
                    value: numericValue ?? 0
                }
            }));
        };

        const handlePriceCurrencyChange = (raw: string) => {
            setFormData((prev) => ({
                ...prev,
                price: {
                    ...prev.price,
                    currencyCode: raw
                }
            }));
        };

        const handleFaceValueAmountChange = (raw: string) => {
            const numericValue = parseInputToNumber(raw, false);
            const next: MoneyValueDto = {
                currencyCode: faceValue?.currencyCode || '',
                value: numericValue ?? 0
            };

            if (
                !next.currencyCode &&
                (numericValue === undefined || numericValue === 0)
            ) {
                setFaceValue(undefined);
            } else {
                setFaceValue(next);
            }
        };

        const handleFaceValueCurrencyChange = (raw: string) => {
            const next: MoneyValueDto = {
                currencyCode: raw,
                value: faceValue?.value ?? 0
            };

            if (!raw && (next.value === 0 || next.value === undefined)) {
                setFaceValue(undefined);
            } else {
                setFaceValue(next);
            }
        };

        const handleSave = async () => {
            if (!isValid) {
                return;
            }
            setError(null);
            const payload: Partial<ProductCreateSupportRequestDto> = {
                ...formData,
                image: formData.image?.objectKey ? formData.image : undefined,
                symbol: formData.symbol?.trim() || undefined,
                externalId: formData.externalId?.trim() || undefined,
                variantName: formData.variantName?.trim() || undefined,
                priceStock: parseInputToNumber(
                    String(formData.priceStock ?? ''),
                    false
                ),
                value:
                    faceValue &&
                    faceValue.value !== undefined &&
                    faceValue.value !== null &&
                    Number.isFinite(faceValue.value) &&
                    Number(faceValue.value) > 0 &&
                    !!faceValue.currencyCode
                        ? {
                              value: Number(faceValue.value),
                              currencyCode: faceValue.currencyCode
                          }
                        : undefined
            };

            try {
                await createProduct.request([
                    payload as ProductCreateSupportRequestDto
                ]);
                onCreated();
                onClose();
                setFormData((prev) => ({
                    ...prev,
                    image: { objectKey: '', bucketName: '' },
                    symbol: '',
                    externalId: '',
                    price: defaultMoney(prev.price.currencyCode, 0),
                    priceStock: undefined,
                    variantName: '',
                    internalState: ProductState.DISABLED
                }));
                setFaceValue(undefined);
            } catch (err: any) {
                console.error('Error creating product:', err);
                setError(
                    err?.response?.data?.message ||
                        err?.message ||
                        'Failed to create product'
                );
            }
        };

        return (
            <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
                <DialogContent className="sm:max-w-3xl">
                    <DialogHeader>
                        <DialogTitle>Add product</DialogTitle>
                    </DialogHeader>

                    <div className="flex max-h-[70vh] flex-col gap-6 overflow-y-auto">
                        {error && (
                            <Alert variant="destructive">
                                <AlertDescription>{error}</AlertDescription>
                            </Alert>
                        )}

                        <div className="flex flex-col gap-2">
                            <p className="text-sm font-medium">
                                Product image (required)
                            </p>
                            <WalletImageUpload
                                imageType={ImageType.PRODUCT}
                                label="Product image"
                                helperText="Upload a 16:9 image (JPG, PNG, WebP) up to 5MB"
                                initialImage={formData.image}
                                onUploaded={(image) =>
                                    setFormData((prev) => ({
                                        ...prev,
                                        image
                                    }))
                                }
                                maxSizeMB={5}
                            />
                        </div>

                        <div className="rounded-lg border p-4">
                            <p className="mb-4 text-sm font-medium">
                                Basic info
                            </p>
                            <div className="grid gap-4 md:grid-cols-2">
                                <TextField
                                    label="Symbol"
                                    type="text"
                                    value={formData.symbol || ''}
                                    onChange={(value) =>
                                        setFormData((prev) => ({
                                            ...prev,
                                            symbol: String(value)
                                        }))
                                    }
                                />
                                <TextField
                                    label="Variant name"
                                    type="text"
                                    value={formData.variantName || ''}
                                    onChange={(value) =>
                                        setFormData((prev) => ({
                                            ...prev,
                                            variantName: String(value)
                                        }))
                                    }
                                />
                                <TextField
                                    label="External ID"
                                    type="text"
                                    value={formData.externalId || ''}
                                    onChange={(value) =>
                                        setFormData((prev) => ({
                                            ...prev,
                                            externalId: String(value)
                                        }))
                                    }
                                />
                                <Select
                                    label="Internal State"
                                    value={formData.internalState}
                                    options={Object.values(ProductState).map(
                                        (state) => ({
                                            label: state,
                                            value: state
                                        })
                                    )}
                                    onChange={(value) =>
                                        setFormData((prev) => ({
                                            ...prev,
                                            internalState: value as ProductState
                                        }))
                                    }
                                />
                            </div>
                        </div>

                        <div className="rounded-lg border p-4">
                            <p className="mb-4 text-sm font-medium">Pricing</p>
                            <div className="grid gap-4 md:grid-cols-2">
                                <TextField
                                    label="Price value"
                                    type="text"
                                    value={formData.price.value}
                                    onChange={(value) =>
                                        handlePriceValueChange(String(value))
                                    }
                                    required
                                />
                                <TextField
                                    label="Price currency"
                                    type="text"
                                    value={formData.price.currencyCode}
                                    onChange={(value) =>
                                        handlePriceCurrencyChange(String(value))
                                    }
                                    required
                                />
                                <TextField
                                    label="Available stock (optional)"
                                    type="text"
                                    value={formData.priceStock ?? ''}
                                    onChange={(value) =>
                                        setFormData((prev) => ({
                                            ...prev,
                                            priceStock:
                                                value === ''
                                                    ? undefined
                                                    : Number(value)
                                        }))
                                    }
                                />
                                <div className="flex gap-4">
                                    <TextField
                                        label="Face value (optional)"
                                        type="text"
                                        value={valueToString(faceValue?.value)}
                                        onChange={(value) =>
                                            handleFaceValueAmountChange(
                                                String(value)
                                            )
                                        }
                                    />
                                    <TextField
                                        label="Face value currency"
                                        type="text"
                                        value={faceValue?.currencyCode || ''}
                                        onChange={(value) =>
                                            handleFaceValueCurrencyChange(
                                                String(value)
                                            )
                                        }
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button
                            variant="ghost"
                            onClick={onClose}
                            disabled={createProduct.loading}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSave}
                            loading={createProduct.loading}
                            disabled={createProduct.loading || !isValid}
                        >
                            {createProduct.loading
                                ? 'Creating...'
                                : 'Create product'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        );
    }
);

export default CreateProductModal;
